import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { usePortal } from '../context/PortalContext';
import { clubService } from '../services/clubService';

type PortalIconName =
  | 'dashboard'
  | 'clubs'
  | 'tables'
  | 'members'
  | 'counter'
  | 'history'
  | 'finance'
  | 'analytics'
  | 'subscription'
  | 'settings'
  | 'menu'
  | 'collapse'
  | 'logout'
  | 'club'
  | 'user';

interface NavItem {
  to: string;
  label: string;
  description: string;
  icon: PortalIconName;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', description: 'Visao executiva do club', icon: 'dashboard' },
  { to: '/clubs', label: 'Clubs', description: 'Contexto e ownership do operador', icon: 'clubs' },
  { to: '/mesas', label: 'Mesas', description: 'Acompanhar status e ocupacao', icon: 'tables' },
  { to: '/membros', label: 'Membros', description: 'Gestao de papeis e acesso', icon: 'members' },
  { to: '/contador', label: 'Contador', description: 'Ajustes operacionais do club', icon: 'counter' },
  { to: '/historico', label: 'Historico', description: 'Hands, board e participantes', icon: 'history' },
  { to: '/financeiro', label: 'Financeiro', description: 'Ledger, depositos e saques', icon: 'finance' },
  { to: '/analytics', label: 'Analytics', description: 'Rake, caixa e custos', icon: 'analytics' },
  { to: '/assinatura', label: 'Assinatura', description: 'Contrato e vigencia do owner', icon: 'subscription' },
  { to: '/configuracoes', label: 'Configuracoes', description: 'Integracao e setup do club', icon: 'settings' },
];

const findActiveItem = (pathname: string): NavItem =>
  NAV_ITEMS.find((item) => pathname === item.to || pathname.startsWith(`${item.to}/`)) || NAV_ITEMS[0];

const getInitials = (value: string): string => {
  const normalized = value.trim();
  if (!normalized) return 'U';
  return normalized
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
};

const PortalIcon: React.FC<{ name: PortalIconName; className?: string }> = ({ name, className }) => {
  const commonProps = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className,
    'aria-hidden': true,
  };

  switch (name) {
    case 'dashboard':
      return (
        <svg {...commonProps}>
          <path d="M4 13.5h6.5V20H4zM13.5 4H20v8h-6.5zM13.5 14.5H20V20h-6.5zM4 4h6.5v6.5H4z" />
        </svg>
      );
    case 'clubs':
      return (
        <svg {...commonProps}>
          <path d="M4 10V6.8A1.8 1.8 0 0 1 5.8 5h12.4A1.8 1.8 0 0 1 20 6.8V10" />
          <path d="M4 10h16v8.2A1.8 1.8 0 0 1 18.2 20H5.8A1.8 1.8 0 0 1 4 18.2z" />
          <path d="M9 10V8h6v2" />
        </svg>
      );
    case 'tables':
      return (
        <svg {...commonProps}>
          <rect x="3.5" y="6.5" width="17" height="11" rx="5.5" />
          <path d="M8 17.5 6.8 20M16 17.5l1.2 2.5M12 9.5v5" />
        </svg>
      );
    case 'members':
      return (
        <svg {...commonProps}>
          <path d="M9 12.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4ZM17 11a2.4 2.4 0 1 0 0-4.8" />
          <path d="M3.8 19.2a5.6 5.6 0 0 1 10.4 0M15.5 18.4a4.4 4.4 0 0 1 4.7-2.7" />
        </svg>
      );
    case 'counter':
      return (
        <svg {...commonProps}>
          <path d="M12 4v16M5 9.5h4M15 14.5h4M6.5 17l3-11h5l3 11" />
        </svg>
      );
    case 'history':
      return (
        <svg {...commonProps}>
          <path d="M4 12a8 8 0 1 0 2.3-5.6" />
          <path d="M4 4v4h4M12 8v4l3 2" />
        </svg>
      );
    case 'finance':
      return (
        <svg {...commonProps}>
          <path d="M5 7.2h14M5 12h14M5 16.8h9" />
          <rect x="3.5" y="4.5" width="17" height="15" rx="3" />
        </svg>
      );
    case 'analytics':
      return (
        <svg {...commonProps}>
          <path d="M5 19.5V10M12 19.5V5.5M19 19.5v-8M3.5 19.5h17" />
        </svg>
      );
    case 'subscription':
      return (
        <svg {...commonProps}>
          <path d="M6 7.5V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1.5" />
          <path d="M4 7.5h16v4.2a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3z" />
          <path d="M9.5 14.7 8.8 20M15.2 14.7l.7 5.3" />
        </svg>
      );
    case 'settings':
      return (
        <svg {...commonProps}>
          <path d="m12 4 1.4 1.8 2.3-.2.8 2.1 2.1.8-.2 2.3L20 12l-1.8 1.4.2 2.3-2.1.8-.8 2.1-2.3-.2L12 20l-1.4-1.8-2.3.2-.8-2.1-2.1-.8.2-2.3L4 12l1.8-1.4-.2-2.3 2.1-.8.8-2.1 2.3.2z" />
          <circle cx="12" cy="12" r="3.2" />
        </svg>
      );
    case 'menu':
      return (
        <svg {...commonProps}>
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      );
    case 'collapse':
      return (
        <svg {...commonProps}>
          <path d="m15 18-6-6 6-6" />
          <path d="M19.5 5v14" />
        </svg>
      );
    case 'logout':
      return (
        <svg {...commonProps}>
          <path d="M9.5 20H6.8A1.8 1.8 0 0 1 5 18.2V5.8A1.8 1.8 0 0 1 6.8 4h2.7" />
          <path d="M14.5 16.5 19 12l-4.5-4.5M10 12h9" />
        </svg>
      );
    case 'club':
      return (
        <svg {...commonProps}>
          <path d="M4 18.5V9.2l8-5.2 8 5.2v9.3" />
          <path d="M9 18.5v-5h6v5" />
        </svg>
      );
    case 'user':
      return (
        <svg {...commonProps}>
          <path d="M12 11.2a3.7 3.7 0 1 0 0-7.4 3.7 3.7 0 0 0 0 7.4Z" />
          <path d="M5 20a7 7 0 0 1 14 0" />
        </svg>
      );
    default:
      return null;
  }
};

const PortalShell: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    loading,
    profile,
    ownedClubs,
    selectedClubId,
    selectedClub,
    setSelectedClubId,
    signOut,
    error,
  } = usePortal();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [subscriptionGateLoading, setSubscriptionGateLoading] = useState(false);
  const [subscriptionGateBlocked, setSubscriptionGateBlocked] = useState(false);
  const [subscriptionGateMessage, setSubscriptionGateMessage] = useState('');

  const activeItem = useMemo(() => findActiveItem(location.pathname), [location.pathname]);
  const userDisplayName = profile?.nickname || profile?.nome || 'Usuario';
  const userRoleLabel = profile?.role ? profile.role.replace(/_/g, ' ') : 'ADMIN';
  const clubName = selectedClub?.name || 'Portal administrativo';
  const clubCode = selectedClub?.code || '3BET';
  const clubMeta = selectedClub
    ? `${selectedClub.total_members} membros | ${selectedClub.total_tables} mesas`
    : 'Escolha um club para carregar os dados';

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname, selectedClubId]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    if (mobileNavOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    document.title = selectedClub
      ? `${activeItem.label} | ${selectedClub.name} | 3BET Clubs`
      : `${activeItem.label} | 3BET Clubs`;
  }, [activeItem.label, selectedClub]);

  useEffect(() => {
    let cancelled = false;

    const checkSubscriptionGate = async () => {
      if (!selectedClub?.id) {
        setSubscriptionGateBlocked(false);
        setSubscriptionGateMessage('');
        return;
      }

      setSubscriptionGateLoading(true);
      try {
        const subscription = await clubService.getClubSubscription(selectedClub.id);
        if (cancelled) return;

        const blocked = !Boolean(subscription?.can_operate_club);
        setSubscriptionGateBlocked(blocked);
        setSubscriptionGateMessage(
          subscription?.regularization_message || 'Regularize a assinatura do club para continuar.'
        );

        if (blocked && location.pathname !== '/assinatura') {
          navigate('/assinatura', { replace: true });
        }
      } catch (nextError: any) {
        if (cancelled) return;
        setSubscriptionGateBlocked(false);
        setSubscriptionGateMessage(nextError?.message || '');
      } finally {
        if (!cancelled) {
          setSubscriptionGateLoading(false);
        }
      }
    };

    checkSubscriptionGate();

    return () => {
      cancelled = true;
    };
  }, [selectedClub?.id, location.pathname, navigate]);

  const handleLogout = () => {
    signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div
      className={[
        'portal-shell',
        sidebarCollapsed ? 'portal-shell--collapsed' : '',
        mobileNavOpen ? 'portal-shell--nav-open' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        className="portal-shell__scrim"
        aria-label="Fechar navegacao"
        onClick={() => setMobileNavOpen(false)}
      />

      <aside className="portal-sidebar">
        <div className="portal-sidebar__header">
          <div className="portal-brand">
            <img
              src="/icones/icone-sem-fundo-com-trangulo.png"
              alt="3BET"
              className="portal-brand__logo"
            />
            <div className="portal-brand__copy">
              <p className="portal-brand__eyebrow">{clubName}</p>
              <h1 className="portal-brand__title">{clubCode}</h1>
              <p className="portal-brand__meta">{clubMeta}</p>
            </div>
          </div>

          <button
            type="button"
            className="portal-icon-button portal-icon-button--desktop"
            aria-label={sidebarCollapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'}
            onClick={() => setSidebarCollapsed((current) => !current)}
          >
            <PortalIcon name="collapse" />
          </button>
        </div>

        <nav className="portal-nav" aria-label="Navegacao principal do portal">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `portal-nav__link${isActive ? ' portal-nav__link--active' : ''}`
              }
            >
              <span className="portal-nav__icon">
                <PortalIcon name={item.icon} />
              </span>
              <span className="portal-nav__copy">
                <strong>{item.label}</strong>
                <small>{item.description}</small>
              </span>
            </NavLink>
          ))}
        </nav>

        <div className="portal-sidebar__footer">
          <div className="portal-sidebar__footer-status">
            <span className="portal-status-dot" />
            <div>
              <strong>Portal online</strong>
              <span>Camada administrativa do owner</span>
            </div>
          </div>

          <div className="portal-sidebar__footer-user">
            <span className="portal-user-chip__avatar">{getInitials(userDisplayName)}</span>
            <div>
              <strong>{userDisplayName}</strong>
              <span>{userRoleLabel}</span>
            </div>
          </div>
        </div>
      </aside>

      <div className="portal-main-frame">
        <header className="portal-topbar portal-topbar--shell">
          <div className="portal-topbar__lead">
            <div className="portal-shell-toolbar">
              <button
                type="button"
                className="portal-icon-button portal-icon-button--mobile"
                aria-label="Abrir navegacao"
                onClick={() => setMobileNavOpen(true)}
              >
                <PortalIcon name="menu" />
              </button>
            </div>

            <div className="portal-topbar__title-block">
              <p className="portal-topbar__eyebrow">{activeItem.label}</p>
              <h2 className="portal-topbar__heading">{clubName}</h2>
              <p className="portal-topbar__subheading">{activeItem.description}</p>
            </div>
          </div>

          <div className="portal-topbar__actions portal-topbar__actions--shell">
            <div className="portal-topbar__club-card">
              <div className="portal-topbar__club-icon">
                <PortalIcon name="club" />
              </div>
              <div className="portal-topbar__club-copy">
                <span className="portal-topbar__club-label">Club ativo</span>
                <strong>{selectedClub ? selectedClub.name : 'Selecione um club'}</strong>
                <small>{selectedClub ? `${selectedClub.code} | ${clubMeta}` : clubMeta}</small>
              </div>
            </div>

            <label className="portal-club-picker portal-club-picker--shell">
              <span>Trocar club</span>
              <select
                value={selectedClubId}
                onChange={(event) => setSelectedClubId(event.target.value)}
                disabled={loading || ownedClubs.length === 0}
              >
                {ownedClubs.length === 0 ? <option value="">Nenhum club</option> : null}
                {ownedClubs.map((club) => (
                  <option key={club.id} value={club.id}>
                    {club.name}
                  </option>
                ))}
              </select>
            </label>

            <div className="portal-user-chip portal-user-chip--shell">
              <span className="portal-user-chip__avatar">{getInitials(userDisplayName)}</span>
              <div className="portal-user-chip__copy">
                <strong className="portal-user-chip__name">{userDisplayName}</strong>
                <span className="portal-user-chip__meta">{userRoleLabel}</span>
              </div>
              <button type="button" className="portal-secondary-button" onClick={handleLogout}>
                <PortalIcon name="logout" className="portal-button-icon" />
                <span>Sair</span>
              </button>
            </div>
          </div>
        </header>

        {error ? <div className="portal-banner portal-banner--error">{error}</div> : null}
        {subscriptionGateBlocked ? (
          <div className="portal-banner portal-banner--warning">
            {subscriptionGateMessage || 'Regularize a assinatura para liberar operacao do club.'}
          </div>
        ) : null}
        {subscriptionGateLoading ? (
          <div className="portal-loading-screen">Validando status da assinatura...</div>
        ) : null}

        <main className="portal-content">
          <Outlet />
        </main>

        <footer className="portal-footer">
          <div className="portal-footer__cluster">
            <span className="portal-status-dot" />
            <div>
              <strong>{clubName}</strong>
              <span>{selectedClub ? `${selectedClub.code} | ${clubMeta}` : clubMeta}</span>
            </div>
          </div>

          <div className="portal-footer__cluster portal-footer__cluster--muted">
            <PortalIcon name="user" className="portal-footer__icon" />
            <div>
              <strong>{userDisplayName}</strong>
              <span>Portal administrativo 3BET</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default PortalShell;
